import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { ADMIN, DRIVER, STAFF, SUPER_ADMIN } from "../../config/constants";
import { getFieldErrors, IFieldError } from "../helpers";
import { IUser, tryConfirmLogin } from "./authReducer";

export interface IStaff extends IUser {
  ordersCount?: number;
  orderRequestsCount?: number;
  pendingOrdersCount?: number;
  pendingServicesPlansCount?: number;
  servicesPlansRequestsCount?: number;
}

export interface IUserWithEmailToken {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  status: string;
}

class RegWorkerErrs {
  form: string | null = null;
  firstName: string | null = null;
  lastName: string | null = null;
  usrImg: string | null = null;
  email: string | null = null;
  phone: string | null = null;
  role: string | null = null;
  vehicleType: string | null = null;
  vehicleNumber: string | null = null;
}

class UpdatePasswordErrs {
  form: string | null = null;
  newPassword: string | null = null;
}

export interface IStaffsState {
  registeringWorker: boolean;
  regWorkerErrs: RegWorkerErrs;
  fetchingStaffs: boolean;
  fetchStaffsErr: string | null;
  staffs: IStaff[];
  fetchingMainStaff: boolean;
  fetchMainStaffErr: string | null;
  mainStaffId: string | null;
  mainStaff: IStaff | null;
  updatingWorker: boolean;
  updatedWorker: boolean;
  updateWorkerErrs: RegWorkerErrs;
  togglingWorkerStatus: boolean;
  toggleWorkerStatusErr: string | null;
  updatingPassword: boolean;
  updatedPassword: boolean;
  updatePasswordErrs: UpdatePasswordErrs;
  fetchingUserWithEmailToken: boolean;
  fetchUserWithEmailTokenErr: string | null;
  userWithEmailToken: (IUserWithEmailToken & IUser) | null;
}

const initialState = {
  registeringWorker: false,
  regWorkerErrs: new RegWorkerErrs(),
  fetchingStaffs: false,
  fetchStaffsErr: null,
  staffs: [],
  fetchingMainStaff: false,
  fetchMainStaffErr: null,
  mainStaffId: null,
  mainStaff: null,
  updatingWorker: false,
  updatedWorker: false,
  updateWorkerErrs: new RegWorkerErrs(),
  togglingWorkerStatus: false,
  toggleWorkerStatusErr: null,
  updatingPassword: false,
  updatedPassword: false,
  updatePasswordErrs: new UpdatePasswordErrs(),
  fetchingUserWithEmailToken: false,
  fetchUserWithEmailTokenErr: null,
  userWithEmailToken: null,
} as IStaffsState;

const staffsSlice = createSlice({
  name: "staffs",
  initialState,
  reducers: {
    clearRegWorkerFieldErr: (
      state: IStaffsState,
      action: PayloadAction<string>,
    ) => {
      state.regWorkerErrs[action.payload as keyof RegWorkerErrs] = null;
    },
    clearRegWorkerErrs: (state: IStaffsState) => {
      state.regWorkerErrs = new RegWorkerErrs();
    },
    tryResetRegWorkerFormErr: (state: IStaffsState) => {
      let shouldReset = true;

      for (let key in state.regWorkerErrs) {
        if (key === "form") {
          continue;
        }

        if (state.regWorkerErrs[key as keyof RegWorkerErrs]) {
          shouldReset = false;
          break;
        }
      }

      if (shouldReset) {
        state.regWorkerErrs.form = null;
      }
    },
    regWorkerStart: (state: IStaffsState) => {
      state.registeringWorker = true;
    },
    regWorkerPass: (state: IStaffsState) => {
      state.registeringWorker = false;
    },
    regWorkerFail: (
      state: IStaffsState,
      action: PayloadAction<IFieldError[]>,
    ) => {
      state.registeringWorker = false;
      const errs = action.payload;

      for (const err of errs) {
        for (const key in state.regWorkerErrs) {
          if (err.field === key) {
            state.regWorkerErrs[key as keyof RegWorkerErrs] = err.msg;
            break;
          }
        }
      }

      if (!state.regWorkerErrs.form) {
        state.regWorkerErrs.form = "Form incorrectly filled.";
      }
    },
    fetchStaffsStart: (state: IStaffsState) => {
      state.fetchingStaffs = true;
    },
    fetchStaffsPass: (state: IStaffsState, action: PayloadAction<IStaff[]>) => {
      state.fetchingStaffs = false;

      state.staffs = action.payload;
    },
    fetchStaffsFail: (state: IStaffsState, action: PayloadAction<string>) => {
      state.fetchingStaffs = false;
      state.fetchStaffsErr = action.payload;
    },
    clearFetchMainStaffErr: (state: IStaffsState) => {
      state.fetchMainStaffErr = null;
    },
    fetchMainStaffStart: (state: IStaffsState) => {
      state.fetchingMainStaff = true;
      state.fetchMainStaffErr = null;
    },
    fetchMainStaffPass: (
      state: IStaffsState,
      action: PayloadAction<IStaff>,
    ) => {
      state.fetchingMainStaff = false;
      state.mainStaff = action.payload;
    },
    fetchMainStaffFail: (
      state: IStaffsState,
      action: PayloadAction<string>,
    ) => {
      state.fetchingMainStaff = false;
      state.fetchMainStaffErr = action.payload;
    },
    setMainStaffId: (
      state: IStaffsState,
      action: PayloadAction<string | null>,
    ) => {
      state.mainStaffId = action.payload;
    },
    setMainStaff: (
      state: IStaffsState,
      action: PayloadAction<IStaff | null>,
    ) => {
      state.mainStaff = action.payload;
    },
    cleartoggleWorkerStatusErr: (state: IStaffsState) => {
      state.toggleWorkerStatusErr = null;
    },
    toggleWorkerStatusStart: (state: IStaffsState) => {
      state.togglingWorkerStatus = true;
      state.toggleWorkerStatusErr = null;
    },
    toggleWorkerStatusPass: (state: IStaffsState) => {
      state.togglingWorkerStatus = false;
    },
    toggleWorkerStatusFail: (
      state: IStaffsState,
      action: PayloadAction<string>,
    ) => {
      state.togglingWorkerStatus = false;
      state.toggleWorkerStatusErr = action.payload;
    },
    clearUpdateWorkerFieldErr: (
      state: IStaffsState,
      action: PayloadAction<string>,
    ) => {
      state.updateWorkerErrs[action.payload as keyof RegWorkerErrs] = null;
    },
    clearUpdateWorkerErrs: (state: IStaffsState) => {
      state.updateWorkerErrs = new RegWorkerErrs();
    },
    tryResetUpdateWorkerFormErr: (state: IStaffsState) => {
      let shouldReset = true;

      for (let key in state.updateWorkerErrs) {
        if (key === "form") {
          continue;
        }

        if (state.updateWorkerErrs[key as keyof RegWorkerErrs]) {
          shouldReset = false;
          break;
        }
      }

      if (shouldReset) {
        state.updateWorkerErrs.form = null;
      }
    },
    resetUpdatedWorker: (state: IStaffsState) => {
      state.updatedWorker = false;
    },
    updateWorkerStart: (state: IStaffsState) => {
      state.updatingWorker = true;
      state.updatedWorker = false;
      state.updateWorkerErrs = new RegWorkerErrs();
    },
    updateWorkerPass: (state: IStaffsState) => {
      state.updatingWorker = false;
      state.updatedWorker = true;
    },
    updateWorkerFail: (
      state: IStaffsState,
      action: PayloadAction<IFieldError[]>,
    ) => {
      state.updatingWorker = false;

      const errs = action.payload;

      for (const err of errs) {
        for (const key in state.updateWorkerErrs) {
          if (err.field === key) {
            state.updateWorkerErrs[key as keyof RegWorkerErrs] = err.msg;
            break;
          }
        }
      }

      if (!state.updateWorkerErrs.form) {
        state.updateWorkerErrs.form = "Form incorrectly filled.";
      }
    },
    clearUpdatePasswordErrs: (state: IStaffsState) => {
      state.updatePasswordErrs = new UpdatePasswordErrs();
    },
    clearUpdatedPassword: (state: IStaffsState) => {
      state.updatedPassword = false;
    },
    updatePasswordStart: (state: IStaffsState) => {
      state.updatingPassword = true;
      state.updatedPassword = false;
      state.updatePasswordErrs = new UpdatePasswordErrs();
    },
    updatePasswordPass: (state: IStaffsState) => {
      state.updatingPassword = false;
      state.updatedPassword = true;
    },
    UpdatePasswordFail: (
      state: IStaffsState,
      action: PayloadAction<IFieldError[]>,
    ) => {
      state.updatingPassword = false;
      const errs = action.payload;

      for (const err of errs) {
        for (const key in state.updatePasswordErrs) {
          if (err.field === key) {
            state.updatePasswordErrs[key as keyof UpdatePasswordErrs] = err.msg;
            break;
          }
        }
      }

      if (!state.updatePasswordErrs.form) {
        state.updatePasswordErrs.form = "Form incorrectly filled.";
      }
    },
    clearFetchUserWithEmailTokenErr: (state: IStaffsState) => {
      state.fetchUserWithEmailTokenErr = null;
    },
    clearUserWithEmailToken: (state: IStaffsState) => {
      state.userWithEmailToken = null;
    },
    fetchUserWithEmailTokenStart: (state: IStaffsState) => {
      state.userWithEmailToken = null;
      state.fetchingUserWithEmailToken = true;
      state.fetchUserWithEmailTokenErr = null;
    },
    fetchUserWithEmailTokenPass: (
      state: IStaffsState,
      action: PayloadAction<IUserWithEmailToken & IUser>,
    ) => {
      state.fetchingUserWithEmailToken = false;
      state.userWithEmailToken = action.payload;
    },
    fetchUserWithEmailTokenFail: (
      state: IStaffsState,
      action: PayloadAction<string>,
    ) => {
      state.fetchingUserWithEmailToken = false;
      state.fetchUserWithEmailTokenErr = action.payload;
    },
  },
});

export const {
  clearRegWorkerFieldErr,
  clearRegWorkerErrs,
  tryResetRegWorkerFormErr,
  regWorkerStart,
  regWorkerPass,
  regWorkerFail,
  fetchStaffsStart,
  fetchStaffsPass,
  fetchStaffsFail,
  clearFetchMainStaffErr,
  fetchMainStaffStart,
  fetchMainStaffPass,
  fetchMainStaffFail,
  setMainStaff,
  setMainStaffId,
  clearUpdateWorkerFieldErr,
  clearUpdateWorkerErrs,
  tryResetUpdateWorkerFormErr,
  resetUpdatedWorker,
  updateWorkerStart,
  updateWorkerPass,
  updateWorkerFail,
  cleartoggleWorkerStatusErr,
  toggleWorkerStatusStart,
  toggleWorkerStatusPass,
  toggleWorkerStatusFail,
  clearUpdatePasswordErrs,
  clearUpdatedPassword,
  updatePasswordStart,
  updatePasswordPass,
  UpdatePasswordFail,
  clearFetchUserWithEmailTokenErr,
  clearUserWithEmailToken,
  fetchUserWithEmailTokenStart,
  fetchUserWithEmailTokenPass,
  fetchUserWithEmailTokenFail,
} = staffsSlice.actions;

interface ITryRegWorkerArg {
  role: string;
  data: FormData;
  authToken: string;
  setRegistered: (truth: boolean) => void;
}

export const tryRegWorker = createAsyncThunk(
  "staffs/tryRegWorker",
  (arg: ITryRegWorkerArg, { dispatch }) => {
    const { role, data, authToken, setRegistered } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/${role}`;

    dispatch(regWorkerStart());

    axios
      .post(endpoint, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        dispatch(regWorkerPass());
        setRegistered(true);
      })
      .catch((err) => {
        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
          fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
          fieldErrors = [{ field: "form", msg: data.err }];
        } else {
          fieldErrors = [
            { field: "form", msg: "Check your network connection." },
          ];
        }

        dispatch(regWorkerFail(fieldErrors));
      });
  },
);

interface ITryfetchStaffsArg {
  authToken: string;
  authUser?: IUser;
}

export const tryFetchStaffs = createAsyncThunk(
  "staffs/tryFetchStaffs",
  (arg: ITryfetchStaffsArg, { dispatch }) => {
    const { authToken, authUser } = arg;

    const rolesStringQuery = `roles[]=driver&roles[]=staff${authUser?.role?.name === SUPER_ADMIN ? "&roles[]=admin" : ""}`;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/users/roles?${rolesStringQuery}`;

    dispatch(fetchStaffsStart());
    axios
      .get(endpoint, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        dispatch(fetchStaffsPass(res.data.staffs));
      })
      .catch((err) => {
        const data = err?.response?.data;

        if (data?.err && data.err === "Session expired") {
          dispatch(tryConfirmLogin({ authToken }));
          return;
        }

        if (data?.err) {
          dispatch(fetchStaffsFail(data.err));
        } else {
          dispatch(fetchStaffsFail("Check your network connection."));
        }
      });
  },
);

interface ITryFetchMainStaffArg {
  authToken: string;
  staffId: string;
}

export const tryFetchMainStaff = createAsyncThunk(
  "staffs/tryFetchMainStaff",
  (arg: ITryFetchMainStaffArg, { dispatch }) => {
    const { authToken, staffId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/staffs/${staffId}`;

    dispatch(fetchMainStaffStart());
    axios
      .get(endpoint, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        const data = res.data;

        dispatch(fetchMainStaffPass(res.data));
      })
      .catch((err) => {
        const data = err?.response?.data;

        if (data?.err && data.err === "Session expired") {
          dispatch(tryConfirmLogin({ authToken }));
          return;
        }

        if (data?.errors) {
          dispatch(fetchMainStaffFail(getFieldErrors(data.errors)[0].msg));
        } else if (data?.err) {
          dispatch(fetchMainStaffFail(data.err));
        } else {
          dispatch(fetchMainStaffFail("Check your network connection."));
        }
      });
  },
);

interface ITryToggleWorkerStatusArg {
  userId: string;
  authToken: string;
  setToggled: (truth: boolean) => void;
}

export const tryToggleWorkerStatus = createAsyncThunk(
  "staffs/toggleWorkerStatus",
  (arg: ITryToggleWorkerStatusArg, { dispatch }) => {
    const { authToken, userId, setToggled } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/users/${userId}/toggle-status`;

    dispatch(toggleWorkerStatusStart());

    axios
      .post(
        endpoint,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        },
      )
      .then((res) => {
        dispatch(toggleWorkerStatusPass());
        setToggled(true);
      })
      .catch((err) => {
        const data = err?.response?.data;

        if (data?.err && data.err === "Session expired") {
          dispatch(tryConfirmLogin({ authToken }));
          return;
        }

        if (data?.err) {
          dispatch(toggleWorkerStatusFail(data.err));
        } else {
          dispatch(toggleWorkerStatusFail("Check your internet connection."));
        }
      });
  },
);

interface ITryUpdateWorkerArg {
  userId: string;
  userRole: string;
  data: FormData;
  authToken: string;
}

export const tryUpdateWorker = createAsyncThunk(
  "workers/tryUpdateWorker",
  (arg: ITryUpdateWorkerArg, { dispatch }) => {
    const { userId, userRole, data, authToken } = arg;

    let resourceType = "";

    switch (userRole) {
      case ADMIN:
        resourceType = "admins";
        break;
      case STAFF:
        resourceType = "staffs";
        break;
      case DRIVER:
        resourceType = "drivers";
        break;
      default:
        break;
    }

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/${resourceType}/${userId}`;

    dispatch(updateWorkerStart());

    axios
      .put(endpoint, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        dispatch(updateWorkerPass(res.data));
      })
      .catch((err) => {
        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
          fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
          fieldErrors = [{ field: "form", msg: data.err }];
        } else {
          fieldErrors = [
            { field: "form", msg: "Check your network connection." },
          ];
        }

        dispatch(updateWorkerFail(fieldErrors));
      });
  },
);

interface ITryUpdatePassword {
  emailToken: string;
  newPassword: string;
  isReseting?: boolean;
}

export const tryUpdatePassword = createAsyncThunk(
  "staffs/updatePassword",
  (arg: ITryUpdatePassword, { dispatch }) => {
    const { emailToken, newPassword, isReseting } = arg;

    let endpoint: string = "";
    let data: { newPassword: string; emailToken?: string } = { newPassword };

    if (isReseting) {
      endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/reset-password/${emailToken}`;
    } else {
      endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/user/update-password`;
      data.emailToken = emailToken;
    }

    dispatch(updatePasswordStart());
    axios
      .post(endpoint, data, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        dispatch(updatePasswordPass());
      })
      .catch((err) => {
        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
          fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
          fieldErrors = [{ field: "form", msg: data.err }];
        } else {
          fieldErrors = [
            { field: "form", msg: "Check your network connection." },
          ];
        }

        dispatch(UpdatePasswordFail(fieldErrors));
      });
  },
);

interface ITryFetchUserWithEmailTokenArg {
  emailToken: string;
  addAuthenticatorSecret?: boolean;
}

export const tryFetchUserWithEmailToken = createAsyncThunk(
  "staffs/tryFetchUserWithEmailToken",
  (arg: ITryFetchUserWithEmailTokenArg, { dispatch }) => {
    const { emailToken, addAuthenticatorSecret } = arg;

    let endpoint = "";

    if (addAuthenticatorSecret) {
      endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/users-with-authenticator-seceret/${emailToken}`;
    } else {
      endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/user-with-email-token?token=${emailToken}`;
    }

    dispatch(fetchUserWithEmailTokenStart());

    axios
      .get(endpoint, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        dispatch(fetchUserWithEmailTokenPass(res.data));
      })
      .catch((err) => {
        const data = err?.response?.data;

        if (data?.errors) {
          dispatch(
            fetchUserWithEmailTokenFail(getFieldErrors(data.errors)[0].msg),
          );
        } else if (data?.err) {
          dispatch(fetchUserWithEmailTokenFail(data.err));
        } else {
          dispatch(
            fetchUserWithEmailTokenFail("Check your network connection."),
          );
        }
      });
  },
);

export default staffsSlice.reducer;
